import React, { useEffect, useState } from 'react';
import { Button, Container, Row, Col, Card, Modal } from 'react-bootstrap';
import { MsalAuthenticationTemplate, useMsal } from '@azure/msal-react';
import { InteractionType } from '@azure/msal-browser';
import { loginRequest } from '../../authConfig';
import { PageLayout2 } from '../../Components/PageLayout/PageLayout2';
import { NotificationsDataService } from '../../Services/NotificationsDataService';
import './UserNotificationsPage.scss';
import { formatGuatemalanDateTime } from '../../Utils/dateFormat';
import Table from '../../Components/Table/Table';
import { formatDateTime } from '../../Utils/formaters';

const UserNotificationsContent = () => {
    const [notifications, setNotifications] = useState([]);
    const [selectedNotification, setSelectedNotification] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const { instance, accounts, inProgress } = useMsal();

    useEffect(() => {
        fetchNotifications();
    }, []);

    const fetchNotifications = async () => {
        try {
            const request = {
                ...loginRequest,
                account: accounts[0]
            };
            const response = await instance.acquireTokenSilent(request);

            const data = await NotificationsDataService.getUserNotifications(response.accessToken);
            setNotifications(data);
        } catch (error) {
            console.error('Failed to fetch notifications:', error);
        }
    };

    const markAsRead = async (notificationId) => {
        try {
            const request = {
                ...loginRequest,
                account: accounts[0]
            };
            const response = await instance.acquireTokenSilent(request);

            await NotificationsDataService.markAsRead(notificationId, response.accessToken);
            fetchNotifications();
        } catch (error) {
            console.error('Failed to mark notification as read:', error);
        }
    };

    const handleNotificationClick = async (id) => {
        try {
            const request = {
                ...loginRequest,
                account: accounts[0]
            };
            const response = await instance.acquireTokenSilent(request);
    
            const notificationWithSas = await NotificationsDataService.getNotificationDetails(id, response.accessToken);
            setSelectedNotification(notificationWithSas); // Use the notification with SAS URL
            setShowModal(true);
        } catch (error) {
            console.error('Failed to fetch notification details with SAS URL:', error);
        }
    };    

    const handleCloseModal = () => {
        setShowModal(false);
        setSelectedNotification(null);
    };

    const isImageFile = (url) => {
        const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp'];
        return imageExtensions.some(ext => url?.toLowerCase().endsWith(ext));
    };

    const columns = React.useMemo(
        () => [
            { header: 'ID', accessorKey: 'id' },
            { header: 'Title', accessorKey: 'title' },
            { header: 'Urgency', accessorKey: 'urgencyLevel' },
            { header: 'Message', accessorKey: 'message' },
            { header: 'Received', accessorKey: 'date', cell: info => formatDateTime(info.getValue()) },
            {
                header: 'Actions',
                id: 'actions',
                cell: ({ row }) => (
                    <div className='d-flex justify-content-end'>
                        {!row.original.isRead ? (
                            <Button size='sm' variant='outline-info text-nowrap' onClick={() => markAsRead(row.original.id)}>
                                Mark as read
                            </Button>
                        ) : (
                            <Button size='sm' variant='success' disabled>
                                Read
                            </Button>
                        )}

                        <Button className='ms-2' size='sm' variant='info' onClick={() => handleNotificationClick(row.original.id)}>
                            View
                        </Button>
                    </div>
                ),
            },
        ],
        []
    );

    return (
        <Container fluid>
            <Row className='h-100'>
                <Col className='flex-grow-1 h-100'>
                    <div className='notifications-container'>
                        <Table columns={columns} data={notifications} tableClass="table-hover" containerClass="table-responsive" headerClass="table-light" />
                    </div>
                </Col>
            </Row>

            {/* Notification Modal */}
            {selectedNotification && (
                <Modal show={showModal} onHide={handleCloseModal} centered dialogClassName="modal-90">
                    <Modal.Header closeButton>

                    </Modal.Header>
                    <Modal.Body>
                        <Card className="border-0">
                            <Card.Body>
                                <div className='d-flex flex-column justify-content-between h-100'>
                                    <div>
                                        <Card.Title>{selectedNotification?.title}</Card.Title>
                                        <Card.Text>{selectedNotification?.message}</Card.Text>
                                        {selectedNotification.attachmentUrl ? (
                                            isImageFile(selectedNotification.attachmentUrl) ? (
                                                <Card.Img variant="top" src={selectedNotification.attachmentUrlSas}
                                                />
                                            ) : (
                                                <div className="text-center">
                                                    <a
                                                        href={selectedNotification?.attachmentUrlSas}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="btn btn-primary mt-3"
                                                    >
                                                        Download Attachment
                                                    </a>
                                                </div>
                                            )
                                        ) : (
                                            <p><em>No attachment available.</em></p>
                                        )}
                                        <Card.Footer>
                                            <small className="text-muted">Sent on {formatDateTime(selectedNotification.date)}</small>
                                        </Card.Footer>
                                    </div>
                                </div>
                            </Card.Body>
                        </Card>
                        <div className='d-flex justify-content-end w-100'>
                            <Button size='md' variant='outline-dark' onClick={handleCloseModal}>Close</Button>
                        </div>
                    </Modal.Body>
                </Modal>
            )}
        </Container>
    );
};


const UserNotificationsPage = () => {
    return (
        <MsalAuthenticationTemplate interactionType={InteractionType.Redirect} authenticationRequest={loginRequest}>
            <PageLayout2 title="Notifications">
                <UserNotificationsContent />
            </PageLayout2>
        </MsalAuthenticationTemplate>
    );
};

export default UserNotificationsPage;
